import { Box, Grid, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TitleComponent from "../../components/widgets/titleComponent";
import PaginationComponent from "../../components/widgets/paginationComponent";
import PlayStoreIcon from '../../assets/icons/play-store.svg';
import AppStoreIcon from '../../assets/icons/app-store.svg';
import WebIcon from '../../assets/icons/website.svg';
import { Link } from "react-router-dom";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const ProjectDetails = ({
    list, 
    px='5%',
    mockupSize='100%'
}) => {
    const theme = useTheme();
    const {t} = useTranslation();
    
    const [galleryIndex, setGalleryIndex] = useState(0);
    
    const [activePage, setActivePage] = useState(0);
        
    const changePage = useCallback((page) => {
        setGalleryIndex(0);
        setActivePage(page);
    }, []);
    
    useEffect(() => {
        if(list){
            setInterval(() => {
                let index = galleryIndex;
                index++;
                index = index > list[activePage].gallery.length - 1 ? 0 : index;
    
                setGalleryIndex(index);
            }, 1000);
        }
    }, [list]);

    return (
        <>
            <Box 
                className="sectionSpacing"
                width='100%'
                sx={{
                    backgroundColor: theme.palette.background.default
                }}
            >
                <TitleComponent title={t('caseStudies.projects')} centerMode/>

                <Stack
                    direction='column'
                    justifyContent='center'
                    alignItems='center'
                    px={px}
                >
                    <Box
                        sx={{
                            width: '100%',
                            p: {xs: 2, md: 5},
                            borderRadius: {xs: 4, md: 8},
                            backgroundColor: list[activePage].theme
                        }}
                    >
                        <Box
                            sx={{
                                borderRadius: {xs: 4, md: 8},
                                backgroundColor: theme.palette.background.default,
                                width: '100%',
                                py: {xs: 4, md: 0}
                            }}
                        >
                            <Grid
                                direction="row"
                                justifyContent='center'
                                alignItems='center'
                                px={{xs: 4, md: 10}}
                                spacing={1}
                                width='100%'
                                container
                            >
                                <Grid size={{xs: 12, lg: !list[activePage].mockup.mobilePortrait ? 6 : 8}}>
                                    <ProjectDetailsView data={list[activePage]} />
                                </Grid>

                                <Grid 
                                    size={{xs: 12, lg: !list[activePage].mockup.mobilePortrait ? 6 : 4}}
                                    direction='row'
                                    display='flex'
                                    justifyContent={{xs: 'center', lg: 'end'}}
                                    px={2}
                                >
                                    {
                                        list[activePage].mockup.mobilePortrait
                                        ?
                                        <MobilePortraitMockUp 
                                            gallery={list[activePage].gallery}
                                            mockupSize={mockupSize}
                                            activePage={activePage}
                                        />
                                        :
                                        <>
                                            {
                                                list[activePage].mockup.mobileLandscape
                                                ?
                                                <MobileLandscapeMockUp 
                                                    gallery={list[activePage].gallery} 
                                                    activePage={activePage}
                                                />
                                                :
                                                <PcMockUp 
                                                    gallery={list[activePage].gallery} 
                                                    activePage={activePage}
                                                />
                                            }
                                        </>
                                    }
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>

                    {/* Pagination */}
                    <PaginationComponent 
                        totalItems={list.length}
                        itemsPerPage={1}
                        activePage={activePage}
                        onPageChange={changePage}
                    />
                </Stack>
            </Box>
        </>
    );
}

export default ProjectDetails;

const ProjectDetailsView = ({data}) => {
    const theme = useTheme();
    const {t} = useTranslation();

    const getReferenceButton = useCallback((url, icon, name) => {
        if(url){
            return <Link
                to={url}
                target="_blank"
            >
                <Box
                    sx={{
                        width: {xs: 30, md: 75},
                        '&: hover': {
                            transform: 'scale(1.1)'
                        }
                    }}
                >
                    <img 
                        src={icon} 
                        alt={name}
                        width='100%'
                    />
                </Box>
            </Link>
        }

        return <></>;
    }, []);

    return (
        <>
            <Stack
                direction='column'
                justifyContent='start'
                alignItems='start'
                spacing={{xs: 1, md: 2}}
                pt={{xs: 1, md: 10}}
                pb={{xs: 0, md: 5}}
                pr={{xs: 0, md: 5}}
            >
                <Stack
                    direction='row'
                    justifyContent='start'
                    alignItems='center'
                    spacing={{xs: 2, md: 5}}
                >
                    <Box
                        width={{xs: 50, md: 100}}
                    >
                        <img 
                            src={data.icon}
                            alt={data.name} 
                            width="100%"
                            style={{
                                borderRadius: '16px',
                                border: `5px solid ${theme.palette.background.paper}`
                            }}
                        />
                    </Box>

                    <Stack
                        direction='column'
                        justifyContent='start'
                        alignItems='start'
                    >
                        <Typography
                            fontWeight={600}
                            fontSize={{xs: 12, md: 16, lg: 24}}
                            textAlign='left'
                            color={theme.palette.text.primary}
                            lineHeight={1.5}
                        >
                            {data.name}
                        </Typography>

                        <Typography
                            fontWeight={600}
                            fontSize={{xs: 12, md: 12, lg: 14}}
                            textAlign="left"
                            lineHeight={2}
                            color={theme.palette.text.secondary}
                        >
                            {t(data.category)}
                        </Typography>
                    </Stack>
                </Stack>

                <Typography
                    fontWeight={500}
                    fontSize={{xs: 12, md: 12, lg: 14}}
                    textAlign={{xs: "justify", md: "left"}}
                    lineHeight={2}
                    color={theme.palette.text.primary}
                >
                    {data.description}
                </Typography>

                <Stack
                    direction='row'
                    justifyContent={{xs: 'center', md: 'start'}}
                    alignItems='start'
                    spacing={{xs: 2, md: 3}}
                    pb={{xs: 2, md: 0}}
                    width='100%'
                >
                    {getReferenceButton(data.reference.playStore, PlayStoreIcon, "PlayStore")}
                    {getReferenceButton(data.reference.appStore, AppStoreIcon, "AppStore")}
                    {getReferenceButton(data.reference.web, WebIcon, "Web")}
                </Stack>
            </Stack>
        </>
    );
}

const MobilePortraitMockUp = ({
    gallery,
    mockupSize,
    activePage
}) => {
    const [galleryIndex, setGalleryIndex] = useState(0);

    const changeGalleryImage = useCallback((dir) => {
        let newIndex = galleryIndex;
        newIndex += 1 * dir;
        newIndex = newIndex > gallery.length - 1 ? 0 : newIndex;
        newIndex = newIndex < 0 ? gallery.length - 1 : newIndex;

        setGalleryIndex(newIndex);
    }, [galleryIndex]);

    useEffect(() => {
        setGalleryIndex(0);
    }, [activePage]);

    return (
        <>
            <Stack
                direction='row'
                justifyContent='center'
                alignItems='center'
                sx={{
                    position: 'relative',
                    width: {xs: '50%', md: mockupSize},
                    transform: `none`
                }}
            >
                {
                    gallery.length > 1
                    ?
                    <>
                        <IconButton 
                            aria-label="prev"
                            sx={{
                                position: 'absolute',
                                left: -50
                            }}
                            onClick={() => changeGalleryImage(-1)}
                        >
                            <ChevronLeftIcon fontSize="large"/>
                        </IconButton>

                        <IconButton 
                            aria-label="after"
                            sx={{
                                position: 'absolute',
                                right: -50
                            }}
                            onClick={() => changeGalleryImage(1)}
                        >
                            <ChevronRightIcon fontSize="large"/>
                        </IconButton>
                    </>
                    :
                    <></>
                }

                <img 
                    src="/images/mockups/mobile/mobileMockupFront.png" 
                    alt="mobile back" 
                    width="100%"
                    style={{
                        position: 'absolute',
                        zIndex: 3
                    }}
                />

                <img 
                    src="/images/mockups/mobile/mobileMockupBack.png" 
                    alt="mobile back" 
                    width="101%"
                    style={{
                        position: 'absolute',
                        zIndex: 2
                    }}
                />

                <img 
                    src={gallery[galleryIndex]}
                    alt="gallery" 
                    width="90%"
                    style={{
                        position: 'relative',
                        aspectRatio: 0.46,
                        zIndex: 2
                    }}
                />
            </Stack>
        </>
    );
}

const MobileLandscapeMockUp = ({
    gallery,
    activePage
}) => {
    const [galleryIndex, setGalleryIndex] = useState(0);

    const changeGalleryImage = useCallback((dir) => {
        let newIndex = galleryIndex;
        newIndex += 1 * dir;
        newIndex = newIndex > gallery.length - 1 ? 0 : newIndex;
        newIndex = newIndex < 0 ? gallery.length - 1 : newIndex;

        setGalleryIndex(newIndex);
    }, [galleryIndex]);

    useEffect(() => {
        setGalleryIndex(0);
    }, [activePage]);

    return (
        <>
            <Stack
                direction='row'
                justifyContent='center'
                alignItems='center'
                sx={{
                    position: 'relative',
                    width: '100%'
                }}
            >
                {
                    gallery.length > 1
                    ?
                    <>
                        <IconButton 
                            aria-label="prev"
                            sx={{
                                position: 'absolute',
                                left: -50
                            }}
                            onClick={() => changeGalleryImage(-1)}
                        >
                            <ChevronLeftIcon fontSize="large"/>
                        </IconButton>

                        <IconButton 
                            aria-label="after"
                            sx={{
                                position: 'absolute',
                                right: -50
                            }}
                            onClick={() => changeGalleryImage(1)}
                        >
                            <ChevronRightIcon fontSize="large"/>
                        </IconButton>
                    </>
                    :
                    <></>
                }

                <img 
                    src="/images/mockups/mobileLandscape/mobileMockupFront.png" 
                    alt="mobile back" 
                    width="100%"
                    style={{
                        position: 'absolute',
                        zIndex: 3
                    }}
                />

                <img 
                    src={gallery[galleryIndex]}
                    alt="gallery" 
                    width="95%"
                    style={{
                        position: 'relative',
                        // aspectRatio: 1.8,
                        zIndex: 2,
                        borderRadius: '16px',
                        display: 'block',
                        objectFit: 'cover'
                    }}
                />
            </Stack>
        </>
    );
}

const PcMockUp = ({
    gallery,
    activePage
}) => {
    const [galleryIndex, setGalleryIndex] = useState(0);

    const changeGalleryImage = useCallback((dir) => {
        let newIndex = galleryIndex;
        newIndex += 1 * dir;
        newIndex = newIndex > gallery.length - 1 ? 0 : newIndex;
        newIndex = newIndex < 0 ? gallery.length - 1 : newIndex;

        setGalleryIndex(newIndex);
    }, [galleryIndex]);

    useEffect(() => {
        setGalleryIndex(0);
    }, [activePage]);

    return (
        <>
            <Stack
                direction='row'
                justifyContent='center'
                alignItems='center'
                sx={{
                    position: 'relative',
                    width: '100%'
                }}
            >
                {
                    gallery.length > 1
                    ?
                    <>
                        <IconButton 
                            aria-label="prev"
                            sx={{
                                position: 'absolute',
                                left: -50
                            }}
                            onClick={() => changeGalleryImage(-1)}
                        >
                            <ChevronLeftIcon fontSize="large"/>
                        </IconButton>

                        <IconButton 
                            aria-label="after"
                            sx={{
                                position: 'absolute',
                                right: -50
                            }}
                            onClick={() => changeGalleryImage(1)}
                        >
                            <ChevronRightIcon fontSize="large"/>
                        </IconButton>
                    </>
                    :
                    <></>
                }

                <img 
                    src="/images/mockups/pc/pcMockup.png" 
                    alt="mobile back" 
                    width="100%"
                    style={{
                        position: 'absolute',
                        zIndex: 3
                    }}
                />

                <img 
                    src={gallery[galleryIndex]}
                    alt="gallery" 
                    width="80%"
                    style={{
                        position: 'relative',
                        // aspectRatio: 1.77,
                        zIndex: 2,
                        display: 'block',
                        objectFit: 'cover'
                    }}
                />
            </Stack>
        </>
    );
}